import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  ApiError,
  api,
  type CategoryNode,
  type ImportDecision,
  type ImportPreview,
  type ImportRow,
} from '../../lib/api';
import { absMoney, dateBR } from '../../lib/format';

/**
 * Importar extrato.
 *
 * A tela tem dois tempos por decisao de projeto, e nao por limitacao: enviar
 * o arquivo NAO grava nada. O que aparece aqui e a conferencia, linha a
 * linha, com o que o sistema achou de cada uma — ja existe, parece um
 * lancamento seu, cai nesta categoria. So o botao de confirmar grava.
 */
export function ImportPage() {
  const queryClient = useQueryClient();
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [decisions, setDecisions] = useState<Record<string, ImportDecision>>({});
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const accounts = useQuery({ queryKey: ['accounts'], queryFn: () => api.accounts() });
  const categories = useQuery({ queryKey: ['categories'], queryFn: () => api.categories() });
  const history = useQuery({ queryKey: ['imports'], queryFn: () => api.imports() });

  const flatCategories = useMemo(
    () => flatten(categories.data?.categories ?? []),
    [categories.data],
  );

  const refreshAll = () => {
    void queryClient.invalidateQueries({ queryKey: ['imports'] });
    void queryClient.invalidateQueries({ queryKey: ['transactions'] });
    void queryClient.invalidateQueries({ queryKey: ['accounts'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    void queryClient.invalidateQueries({ queryKey: ['rules'] });
  };

  const upload = useMutation({
    mutationFn: (payload: { file: File; accountId: string }) => api.uploadStatement(payload),
    onSuccess: (result) => {
      setPreview(result);
      setDecisions({});
      setError(null);
      setDone(null);
      void queryClient.invalidateQueries({ queryKey: ['imports'] });
    },
    onError: (caught) => setError(messageFor(caught)),
  });

  const confirm = useMutation({
    mutationFn: (batchId: string) => api.commitImport(batchId, Object.values(decisions)),
    onSuccess: (result) => {
      setPreview(null);
      setDecisions({});
      setError(null);
      setDone(
        `${result.imported} lancamento(s) importado(s), ${result.reconciled} conciliado(s) e ${result.skipped} pulado(s).`,
      );
      refreshAll();
    },
    onError: (caught) => setError(messageFor(caught)),
  });

  const discard = useMutation({
    mutationFn: (batchId: string) => api.discardImport(batchId),
    onSuccess: () => {
      setPreview(null);
      setDecisions({});
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['imports'] });
    },
    onError: (caught) => setError(messageFor(caught)),
  });

  const setDecision = (row: ImportRow, patch: Partial<ImportDecision>): void => {
    setDecisions((current) => {
      const base: ImportDecision = current[row.id] ?? {
        rowId: row.id,
        action: defaultAction(row.status),
        ...(row.reconcileTransactionId
          ? { reconcileTransactionId: row.reconcileTransactionId }
          : {}),
      };
      return { ...current, [row.id]: { ...base, ...patch } };
    });
  };

  const actionOf = (row: ImportRow): ImportDecision['action'] =>
    decisions[row.id]?.action ?? defaultAction(row.status);

  const categoryOf = (row: ImportRow): string => {
    const chosen = decisions[row.id]?.categoryId;
    if (chosen !== undefined) return chosen ?? '';
    return row.suggestedCategoryId ?? '';
  };

  return (
    <div className="main">
      <header className="page-header">
        <h1>Importar extrato</h1>
        <p>
          Envie o OFX ou o CSV do banco. Nada e gravado no envio: voce confere linha a linha antes
          de confirmar.
        </p>
      </header>

      {error && (
        <div className="alert error" role="alert">
          {error}
        </div>
      )}

      {done && (
        <div className="alert success" role="status">
          {done}
        </div>
      )}

      {!preview && (
        <UploadForm
          accounts={accounts.data?.accounts ?? []}
          busy={upload.isPending}
          onSubmit={(payload) => upload.mutate(payload)}
        />
      )}

      {preview && (
        <section className="card">
          <div className="between" style={{ marginBottom: 'var(--space-4)' }}>
            <div>
              <h2 className="card-title">Conferencia</h2>
              <p className="card-hint" style={{ marginBottom: 0 }}>
                {preview.batch.filename} — {preview.batch.accountName} ({preview.batch.source})
                {preview.batch.periodStart && preview.batch.periodEnd
                  ? `, de ${dateBR(preview.batch.periodStart)} a ${dateBR(preview.batch.periodEnd)}`
                  : ''}
              </p>
            </div>
            <div className="row">
              <button
                className="btn"
                onClick={() => confirm.mutate(preview.batch.id)}
                disabled={confirm.isPending}
              >
                {confirm.isPending ? 'Confirmando...' : 'Confirmar importacao'}
              </button>
              <button
                className="btn ghost"
                onClick={() => discard.mutate(preview.batch.id)}
                disabled={discard.isPending}
              >
                Descartar
              </button>
            </div>
          </div>

          <div className="grid" style={{ marginBottom: 'var(--space-4)' }}>
            <Counter label="Novos" value={preview.batch.counts.novos} />
            <Counter label="Ja existiam" value={preview.batch.counts.duplicados} />
            <Counter label="A conciliar" value={preview.batch.counts.conciliar} />
            <Counter label="Marcados para pular" value={preview.batch.counts.ignorados} />
          </div>

          {preview.batch.problems.length > 0 && (
            <div className="alert warning">
              <strong>Linhas que nao deu para ler:</strong>
              <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                {preview.batch.problems.map((problem) => (
                  <li key={problem}>{problem}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Descricao</th>
                  <th style={{ textAlign: 'right' }}>Valor</th>
                  <th>Situacao</th>
                  <th>Categoria</th>
                  <th>O que fazer</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((row) => (
                  <tr key={row.id}>
                    <td className="tabular">{dateBR(row.date)}</td>
                    <td>
                      {row.description}
                      {row.warning && (
                        <div
                          className="muted"
                          style={{ fontSize: 'var(--text-xs)', color: 'var(--color-warning)' }}
                        >
                          {row.warning}
                        </div>
                      )}
                      {row.reconcileDescription && (
                        <div className="muted" style={{ fontSize: 'var(--text-xs)' }}>
                          Parece: {row.reconcileDescription}
                          {row.reconcileDate ? ` (${dateBR(row.reconcileDate)})` : ''}
                        </div>
                      )}
                    </td>
                    <td
                      className={`money ${row.kind === 'EXPENSE' ? 'expense' : 'income'}`}
                      style={{ textAlign: 'right' }}
                    >
                      {row.kind === 'EXPENSE' ? '-' : '+'}
                      {absMoney(row.amountCents)}
                    </td>
                    <td className="muted">{STATUS_LABELS[row.status]}</td>
                    <td>
                      <select
                        value={categoryOf(row)}
                        onChange={(event) =>
                          setDecision(row, { categoryId: event.target.value || null })
                        }
                        aria-label={`Categoria da linha ${row.lineNumber}`}
                        disabled={actionOf(row) !== 'IMPORT'}
                      >
                        <option value="">Sem categoria</option>
                        {flatCategories
                          .filter((category) => category.kind === row.kind)
                          .map((category) => (
                            <option key={category.id} value={category.id}>
                              {category.path}
                            </option>
                          ))}
                      </select>
                      {row.matchedRuleName && (
                        <div className="muted" style={{ fontSize: 'var(--text-xs)' }}>
                          regra {row.matchedRuleName}
                        </div>
                      )}
                    </td>
                    <td>
                      <select
                        value={actionOf(row)}
                        onChange={(event) =>
                          setDecision(row, {
                            action: event.target.value as ImportDecision['action'],
                          })
                        }
                        aria-label={`Acao da linha ${row.lineNumber}`}
                      >
                        <option value="IMPORT">Importar</option>
                        <option value="SKIP">Pular</option>
                        {row.reconcileTransactionId && <option value="RECONCILE">Conciliar</option>}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="card">
        <h2 className="card-title">Importacoes anteriores</h2>

        {(history.data?.batches ?? []).length === 0 ? (
          <p className="card-hint" style={{ marginBottom: 0 }}>
            Nenhuma ainda.
          </p>
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Arquivo</th>
                  <th>Conta</th>
                  <th>Situacao</th>
                  <th style={{ textAlign: 'right' }}>Importados</th>
                  <th style={{ textAlign: 'right' }}>Conciliados</th>
                </tr>
              </thead>
              <tbody>
                {(history.data?.batches ?? []).map((batch) => (
                  <tr key={batch.id}>
                    <td>{batch.filename}</td>
                    <td className="muted">{batch.accountName}</td>
                    <td className="muted">{BATCH_LABELS[batch.status]}</td>
                    <td className="tabular" style={{ textAlign: 'right' }}>
                      {batch.importedCount}
                    </td>
                    <td className="tabular" style={{ textAlign: 'right' }}>
                      {batch.reconciledCount}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

const STATUS_LABELS: Record<ImportRow['status'], string> = {
  NEW: 'Novo',
  DUPLICATE: 'Ja existe',
  RECONCILE: 'Parece que ja lancou',
  IGNORED: 'Sugerido pular',
};

const BATCH_LABELS: Record<string, string> = {
  PREVIEW: 'Em conferencia',
  APPLIED: 'Confirmada',
  DISCARDED: 'Descartada',
};

function defaultAction(status: ImportRow['status']): ImportDecision['action'] {
  if (status === 'NEW') return 'IMPORT';
  if (status === 'RECONCILE') return 'RECONCILE';
  return 'SKIP';
}

function Counter({ label, value }: { label: string; value: number }) {
  return (
    <article className="stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value tabular">{value}</div>
    </article>
  );
}

function UploadForm({
  accounts,
  busy,
  onSubmit,
}: {
  accounts: Array<{ id: string; name: string; archived: boolean }>;
  busy: boolean;
  onSubmit: (payload: { file: File; accountId: string }) => void;
}) {
  const [accountId, setAccountId] = useState('');
  const [file, setFile] = useState<File | null>(null);

  const usable = accounts.filter((account) => !account.archived);

  return (
    <section className="card">
      <h2 className="card-title">Enviar arquivo</h2>
      <p className="card-hint">
        No internet banking, procure por &quot;exportar extrato&quot; e escolha OFX quando houver:
        ele traz o identificador de cada lancamento, e com isso a reimportacao nunca duplica.
      </p>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (file && accountId) onSubmit({ file, accountId });
        }}
      >
        <div className="row">
          <div className="field" style={{ flex: 1, minWidth: 220 }}>
            <label htmlFor="import-account">Conta que recebe o extrato</label>
            <select
              id="import-account"
              value={accountId}
              onChange={(event) => setAccountId(event.target.value)}
              required
            >
              <option value="">Escolha a conta</option>
              {usable.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name}
                </option>
              ))}
            </select>
          </div>

          <div className="field" style={{ flex: 1, minWidth: 220 }}>
            <label htmlFor="import-file">Arquivo</label>
            <input
              id="import-file"
              type="file"
              accept=".ofx,.qfx,.csv,.txt"
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
              required
            />
          </div>
        </div>

        <button className="btn" type="submit" disabled={busy || !file || !accountId}>
          {busy ? 'Lendo o arquivo...' : 'Conferir'}
        </button>
      </form>
    </section>
  );
}

interface FlatCategory {
  id: string;
  path: string;
  kind: 'INCOME' | 'EXPENSE';
}

function flatten(nodes: CategoryNode[], prefix = ''): FlatCategory[] {
  return nodes.flatMap((node) => {
    const path = prefix ? `${prefix} / ${node.name}` : node.name;
    return [{ id: node.id, path, kind: node.kind }, ...flatten(node.children, path)];
  });
}

function messageFor(caught: unknown): string {
  if (caught instanceof ApiError) return caught.message;
  return 'Nao consegui concluir. Tente de novo.';
}
